import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function AddLandlordModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    if (open) {
      loadProperties();
    }
  }, [open]);

  const loadProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to load properties:', error);
    }
  };

  const toggleProperty = (propertyId) => {
    if (selectedProperties.includes(propertyId)) {
      setSelectedProperties(selectedProperties.filter(id => id !== propertyId));
    } else {
      setSelectedProperties([...selectedProperties, propertyId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Generate temporary password
      const tempPassword = 'Temp' + Math.random().toString(36).slice(2, 10) + '!';
      
      // Create landlord with properties
      await axios.post(`${API}/landlords/create-with-properties`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: tempPassword,
        property_ids: selectedProperties
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Landlord created successfully! Welcome email sent with login credentials.');
      onSuccess();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: ''
      });
      setSelectedProperties([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create landlord');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Landlord</DialogTitle>
          <DialogDescription>Create landlord account and profile</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Personal Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Sarah Johnson"
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="sarah@example.com"
                  required
                />
              </div>
            </div>

            <div className="mt-3">
              <Label>Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="0400 123 456"
              />
            </div>
          </div>

          {/* Property Assignment */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Assign Properties (Optional)</h3>
            <p className="text-sm text-gray-600 mb-3">
              Select existing properties owned by this landlord, or leave empty and add properties later.
            </p>
            <div className="bg-white border border-green-200 rounded p-2 mb-3">
              <p className="text-xs text-green-800">
                💡 <strong>Tip:</strong> You can create the landlord first, then add their properties on the Properties page.
              </p>
            </div>
            
            {properties.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No properties available to assign</p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {properties.map((property) => (
                  <div
                    key={property.id}
                    onClick={() => toggleProperty(property.id)}
                    className={`p-3 border rounded cursor-pointer transition ${
                      selectedProperties.includes(property.id)
                        ? 'bg-green-100 border-green-500'
                        : 'bg-white border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{property.address}</p>
                        <p className="text-sm text-gray-600">{property.suburb}, {property.state} {property.postcode}</p>
                        <p className="text-xs text-gray-500 mt-1">{property.bedrooms} bed • {property.bathrooms} bath</p>
                      </div>
                      {selectedProperties.includes(property.id) && (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white">
                          ✓
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedProperties.length > 0 && (
              <div className="mt-3 p-2 bg-white rounded border border-green-300">
                <p className="text-sm font-medium text-green-900">{selectedProperties.length} {selectedProperties.length === 1 ? 'property' : 'properties'} selected</p>
              </div>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-900">
              <strong>📧 Email Notification:</strong> A welcome email with login credentials will be automatically sent to the landlord's email address.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Landlord & Send Welcome Email'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}